import {
    AnnotationConfig,
    AnnotationState,
    SvgAnnotationsEditorConfig,
} from 'shared/svg_annotations/types';

export const NewAnnotationState: AnnotationState = { type: 'new' };
export const ReadonlyAnnotationState: AnnotationState = { type: 'readonly' };

export const StrokeWidth = 1.75;
export const DragPointClickRadius = 10;
export const DragPointRadius = 3;
export const SelectionStrokeWidth = 10;
export const PinTextFontSize = 12;
export const PinTextFontFamily = 'REM';
export const StrokeDasharray = `4 4`;
export const PinLineLength = 35;
export const PinSize = 10;

export const ArrowWidth = 8;
export const ArrowHeight = 7;

export const ArrowPoints = [
    `0 ${ArrowHeight / 2}`,
    `${ArrowWidth} 0`,
    `${ArrowWidth} ${ArrowHeight}`,
].join(', ');

export const getAnnotationState = (
    config: AnnotationConfig,
    editorConfig?: SvgAnnotationsEditorConfig,
): AnnotationState => {
    if (!editorConfig) {
        return ReadonlyAnnotationState;
    }
    if (editorConfig.selectedAnnotationId === config.id) {
        return {
            type: 'selected',
            onUpdate: (newConfig) => editorConfig.onUpdate(newConfig, true),
        };
    }
    return { type: 'selectable', onSelect: () => editorConfig.onSelect(config) };
};

export const getSvgCursor = (
    editorConfig?: SvgAnnotationsEditorConfig,
): React.CSSProperties['cursor'] => {
    if (!editorConfig) {
        return undefined;
    }
    if (editorConfig.selectedAnnotationId) {
        return 'default';
    }
    return 'crosshair';
};

export const addStrokeAnimation = (element: SVGElement | null) => {
    if (element) {
        element.style.strokeDasharray = StrokeDasharray;

        element.animate([{ strokeDashoffset: 0 }, { strokeDashoffset: 8 }], {
            duration: 500,
            iterations: Infinity,
        });
    }
};

export const removeStrokeAnimation = (element: SVGElement | null) => {
    if (element) {
        element.style.strokeDasharray = '0';
        element.animate([]);
    }
};

type EventLike = Readonly<{
    stopPropagation: () => void;
    preventDefault: () => void;
}>;

export const stopEvent = (event: EventLike) => {
    event.stopPropagation();
    event.preventDefault();
};

export const constrainValue = (value: number, min: number, max: number) =>
    Math.min(Math.max(min, value), max);

export const calcPosition = (widthOrHeight: number, valuePx: number) =>
    (valuePx / widthOrHeight) * 100;

export const calcPixel = (widthOrHeight: number, valuePercentage: number) =>
    (valuePercentage / 100) * widthOrHeight;

export const degreesToRadians = (degrees: number) => degrees * (Math.PI / 180);

export const radiansToDegrees = (radians: number) => radians * (180 / Math.PI);

export const getAngleInDegrees = (x1: number, y1: number, x2: number, y2: number) =>
    radiansToDegrees(Math.atan2(-(y2 - y1), x2 - x1));

export const polarToPixel = (degrees: number, radius: number) => {
    const radians = degreesToRadians(degrees);

    return {
        x: Math.cos(radians) * radius,
        y: Math.sin(-radians) * radius,
    };
};

export type Rectangle = Readonly<{ x1: number; y1: number; x2: number; y2: number }>;

export const normalizeDragMoveArea = (event: Rectangle): Rectangle => {
    const x1 = Math.min(event.x1, event.x2);
    const y1 = Math.min(event.y1, event.y2);
    const x2 = Math.max(event.x1, event.x2);
    const y2 = Math.max(event.y1, event.y2);

    if (x1 >= 0 && y1 >= 0 && x2 <= 100 && y2 <= 100) {
        return event;
    }
    if (x1 < 0 && x2 > 100) {
        return normalizeDragMoveArea({ x1: 0, y1, x2: 100, y2 });
    }
    if (y1 < 0 && y2 > 100) {
        return normalizeDragMoveArea({ x1, y1: 0, x2, y2: 100 });
    }
    if (x1 < 0) {
        return normalizeDragMoveArea({ x1: 0, y1, x2: x2 - x1, y2 });
    }
    if (x2 > 100) {
        return normalizeDragMoveArea({ x1: x1 - (x2 - 100), y1, x2: 100, y2 });
    }
    if (y1 < 0) {
        return normalizeDragMoveArea({ x1, y1: 0, x2, y2: y2 - y1 });
    }
    if (y2 > 100) {
        return normalizeDragMoveArea({ x1, y1: y1 - (y2 - 100), x2, y2: 100 });
    }
    throw new Error('Should not get here');
};

export const getTargetOffset = (event: PointerEvent, handleOffset: boolean) => {
    if (!handleOffset || !(event.target instanceof Element)) {
        return { xOffset: 0, yOffset: 0 };
    }

    const targetRect = event.target.getBoundingClientRect();
    const targetMidX = (targetRect.left + targetRect.right) / 2;
    const targetMidY = (targetRect.top + targetRect.bottom) / 2;
    const xOffset = event.pageX - targetMidX;
    const yOffset = event.pageY - targetMidY;

    return { xOffset, yOffset };
};
